import { useLocation, useNavigate } from "react-router-dom";
import { LabeledInput, BigButton, Stack } from "../../components";
import { useTranslation } from "../../language";
import { IRegisterState, RegisterType } from "../../models/registerState";
import Keyboard, { KeyboardReactInterface } from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import { useEffect, useRef, useState, useCallback } from "react";

import { useRequest } from "../../http";
import './customKeyboardStyle.scss'

export const RegisterPage = () => {
	const translate = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const state = location.state as IRegisterState;

	const [birthday, setBirthday] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [keyboardVisible, setKeyboardVisible] = useState(true);

	const [layoutName, setLayoutName] = useState("numeric");
	const inactiveTimeoutSeconds = 30;

	const keyboardDisplaySet = {
		NEXT: { '{bksp}': '←', '{space}': ' ', '{shift}': '⇧', '{num}': '123', '{enter}': translate('Register.Keyboard.Next'), '{chars}': 'ABC' },
		FINISH: { '{bksp}': '←', '{space}': ' ', '{shift}': '⇧', '{num}': '123', '{enter}': translate('Register.Keyboard.Finish'), '{chars}': 'ABC' }
	};

	const [keyboardDisplay, setKeyboardDisplay] = useState(keyboardDisplaySet.NEXT);

	const keyboard = useRef<KeyboardReactInterface>();
	const birthdayRef = useRef<HTMLInputElement>();
	const firstnameRef = useRef<HTMLInputElement>();
	const lastnameRef = useRef<HTMLInputElement>();
	const stepRef = useRef(1);
	const timerRef = useRef<any>();
	// const [step, setStep] = useState(1);

	const { execute: submitPatientToDoctorQueue } = useRequest<any>('SubmitAsPatientToQueue');
	const { execute: submitPatientToOtherQueue } = useRequest<any>('SubmitAsPatientToOtherQueue')

	const layouts = {
		default: ['q w e r t y u i o p å ¨', 'a s d f g h j k l ø æ', '{shift} z x c v b n m - {bksp}', '{num} {space} {enter}'],
		shift: ['Q W E R T Y U I O P Å ¨', 'A S D F G H J K L Ø Æ', '{shift} Z X C V B N M - {bksp}', '{num} {space} {enter}'],
		numeric: ['7 8 9', '4 5 6', '1 2 3', '{chars} 0 {bksp}'],
		numericLocked: ['7 8 9', '4 5 6', '1 2 3', ' 0 {bksp}'],
	};

	const initiateNewTimeout = useCallback(() => {
		const timer = () => setTimeout(() => {
			navigate("/");
		}, inactiveTimeoutSeconds * 1000);

		timerRef.current = timer();

		return () => {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, [navigate]);

	useEffect(() => {
		return initiateNewTimeout();
	}, [initiateNewTimeout]);


	const onChange = (input: string) => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			initiateNewTimeout()
		}

		// LastName
		if (stepRef.current === 3) {
			setLayoutName(input.length > 0 ? "default" : "shift");
			setLastName(input);
			return;
		}

		// FirstName
		if (stepRef.current === 2) {
			setLayoutName(input.length > 0 ? "default" : "shift");
			setFirstName(input);
			return;
		}

		// Birthday
		if (stepRef.current === 1) {
			// Process birthdate for displaying:
			// Special stuff happens if backspace:
			const isRemoving = input.length < birthday.length;

			// 1. Remove chars that are not allowed
			if (!isRemoving && (Number.isNaN(Number(input[input.length - 1])) || input.length > 8)) {
				input = input.slice(0, -1);
				keyboard.current?.setInput(input);
				return;
			}

			let removeLast = false;
			// Check for illegal dates
			switch (input.length) {
				case 1:
					if (Number(input.slice(input.length - 1, input.length)) > 3) {
						removeLast = true;
					}
					break;
				case 2:
					if (Number(input.slice(input.length - 2, input.length)) > 31) {
						removeLast = true;
					}
					break;
				case 3:
					break;
				case 4:
					let last1 = Number(input.slice(input.length - 1, input.length));
					if (last1 > 1) {
						removeLast = true;
					}
					break;
				case 5:
					if (Number(input.slice(input.length - 2, input.length)) > 12) {
						removeLast = true;
					}
					break;
				case 6:
					break;
				case 7: // DD-MM-Y
					break;
				case 8: // DD-MM-YY
					break;
				// case 7:
				// 	let last = Number(input.slice(input.length - 1, input.length));
				// 	if (last > 2 || last < 1) {
				// 		removeLast = true;
				// 	}
				// 	break;
				// case 8:
				// 	let lastTwo = Number(input.slice(input.length - 2, input.length));
				// 	if (lastTwo < 19 || lastTwo > 20) {
				// 		removeLast = true;
				// 	}
				// 	break;
				// case 9:
				// 	let lastThree = Number(input.slice(input.length - 3, input.length));
				// 	if (lastThree > 202) {
				// 		removeLast = true;
				// 	}
				// 	break;
				// case 10:
				// 	let year = Number(input.slice(input.length - 4, input.length));
				// 	if (year < 1900 || year > new Date().getFullYear()) {
				// 		removeLast = true;
				// 	}
				// break;
			}
			if (removeLast) {
				input = input.slice(0, -1);
				keyboard.current?.setInput(input);
				setBirthday(input);
				return;
			}

			if (isRemoving && birthday[birthday.length - 1] === "-") {
				input = input.slice(0, -1);
				keyboard.current?.setInput(input);
				setBirthday(input);
				return;
			}

			// 2. Add hyphen to end to make it correct
			if (!isRemoving) {
				let added = false;
				for (let i = 0; i < input.length; i++) {
					if (i === input.length - 1 && (i === 1 || i === 4)) {
						input += '-';
						added = true;
					}
				}

				if (added) {
					keyboard.current?.setInput(input);
				}
			}

			setBirthday(input);
			if (input.length === 8) {
				goToStep(2);
			}
			return;
		}
	};

	const onKeyPress = (key: any) => {
		if (key === "{enter}") {
			if (stepRef.current === 2) {
				goToStep(3); //Might want timeout for keyboard value here..
			} else if (stepRef.current === 3) { // "complete"
				onFinished();
			} 
		} else if (key === "{shift}") {
			if (layoutName === "default") {
				setLayoutName("shift");
			} else {
				setLayoutName("default");
			}
		} else if (key === "{caps}") {
			setLayoutName("shift");
		} else if (key === "{num}") {
			setLayoutName("numeric");
		} else if (key === '{chars}') {
			setLayoutName("default");
		}

		setTimeout(() => {
			updateInputFocus();
		});
	}

	const updateInputFocus = () => {
		switch (stepRef.current) {
			case 1: birthdayRef.current?.focus(); break;
			case 2: firstnameRef.current?.focus(); break;
			case 3: lastnameRef.current?.focus(); break;
		}
	}

	const goToStep = (s: number) => {
		stepRef.current = s;

		switch (stepRef.current) {
			case 1:
				setLayoutName("numericLocked");
				break;
			case 2:
				setLayoutName(firstName.length === 0 ? "shift" : "default");
				setKeyboardDisplay(keyboardDisplaySet.NEXT);
				break;
			case 3:
				setLayoutName(lastName.length === 0 ? "shift" : "default");
				setKeyboardDisplay(keyboardDisplaySet.FINISH);
				break;
		}

		updateKeyboardValue();
		setTimeout(updateInputFocus, 1);
	}

	if (birthdayRef.current) {
		birthdayRef.current.onfocus = () => {
			if (stepRef.current !== 1)
				goToStep(1);
		}
	}

	if (firstnameRef.current) {
		firstnameRef.current.onfocus = () => {
			if (stepRef.current !== 2)
				goToStep(2);
		}
	}

	if (lastnameRef.current) {
		lastnameRef.current.onfocus = () => {
			if (stepRef.current !== 3)
				goToStep(3);
		}
	}

	const updateKeyboardValue = () => {
		switch (stepRef.current) {
			case 1:
				keyboard.current?.setInput(birthday);
				setBirthday(birthday);
				break;
			case 2:
				keyboard.current?.setInput(firstName);
				if (firstName.length < 1) {
					setLayoutName("shift");
				}
				setFirstName(firstName);
				break;
			case 3:
				keyboard.current?.setInput(lastName);
				if (lastName.length < 1) {
					setLayoutName("shift");
				}
				setLastName(lastName);
				break;
		}
	}

	const onFinished = async () => {
		setKeyboardVisible(false);
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}

		// send fdate, fname, lname 
		let data = { numberInQueue: 0 };

		switch (state.type) {
			case RegisterType.Doctor:
				data = await submitPatientToDoctorQueue({ Id: birthday, FirstName: firstName, LastName: lastName, PhoneNumber: '81549300', QueueName: state.room?.queueName.value, DoctorId: state.employee?.id.value });
				break;
			case RegisterType.Lab:
				data = await submitPatientToOtherQueue({ Id: birthday, FirstName: firstName, LastName: lastName, PhoneNumber: '81549300', RoomType: 0 });
				break;
			case RegisterType.EmergencyRoom:
				data = await submitPatientToOtherQueue({ Id: birthday, FirstName: firstName, LastName: lastName, PhoneNumber: '81549300', RoomType: 4 });
				break;
		}

		(window as any).printer.sendToPrinter({ numberInQueue: data.numberInQueue });
		navigate("/queueNumber/" + data.numberInQueue);
	};

	if (state === null) {
		return (<></>);
	}

	const renderInputs = () => {
		return <div style={{ display: 'flex', flexDirection: 'column', /*flex: '1 1 auto',*/ minWidth: '500px', maxWidth: '900px', margin: 'auto' }}>
			<LabeledInput
				id="bd"
				label={translate("Register.Form.Label.Birthday")}
				value={birthday}
				placeholder={translate("Register.Form.Placeholder.Birthday")}
				show={true}
				ref={birthdayRef}
			/>

			<LabeledInput
				id="fn"
				label={translate("Register.Form.Label.FirstName")}
				placeholder={translate("Register.Form.Placeholder.FirstName")}
				value={firstName}
				show={true}
				ref={firstnameRef}
			/>

			<LabeledInput
				id="ln"
				label={translate("Register.Form.Label.LastName")}
				placeholder={translate("Register.Form.Placeholder.LastName")}
				value={lastName}
				show={true}
				ref={lastnameRef}
			/>
			<Stack horizontal horizontalAlign="center" style={{ padding: '1rem 0', minHeight: '150px' }}>
				{/* <BigButton
          text={translate("Register.Form.Button.Previous")}
          onClick={() => {
            if (stepRef.current > 1) {
              goToStep(stepRef.current - 1)
            } else {
              updateInputFocus();
            }
          }}
        />

        <BigButton
          text={translate("Register.Form.Button.Next")}
          onClick={() => {
            if (stepRef.current < 3) {
              goToStep(stepRef.current + 1);
            } else {
              updateInputFocus();
            }
          }}
        /> */}

				{(stepRef.current > 2 || lastName) &&
					<BigButton
						text={translate("Register.Form.Button.Finished")}
						onClick={onFinished}
					/>
				}
			</Stack>

		</div>
	}

	const renderKeyboard = () => (
		<div style={{  flex: '1 1 auto'}}>
			<Keyboard
				keyboardRef={r => (keyboard.current = r)}
				layoutName={layoutName}
				layout={layouts}
				display={keyboardDisplay}
				onChange={onChange}
				onKeyPress={onKeyPress}
				onKeyPres={(input: String) => { console.log("OnKeyPress", input) }}

/>
		</div>
	);

	return (
		<>
			<div style={{ paddingLeft: '1rem', paddingRight: '1rem', display: 'flex', flexDirection: 'column', height: '100%'}}>
				{state.type === RegisterType.Doctor ? <h3 style={{ userSelect: 'none', textAlign: 'center' }}>{translate("Register.RegisterForDoc", { doctor: state.employee?.name })}</h3> : null}
				{state.type === RegisterType.EmergencyRoom ? <h3 style={{ userSelect: 'none', textAlign: 'center' }}>{translate("Register.RegisterForEmergencyRoom")}</h3> : null}
				{renderInputs()}
				{keyboardVisible ? renderKeyboard() : <div style={{flex:'1 1 auto'}}></div>}
			</div>
		</>
	)
}